/**
 * Local authentication service.
 * Handles password-based accounts and TOTP MFA entirely client-side.
 * Passwords are hashed with PBKDF2-SHA256 (600k iterations) via Web Crypto.
 * TOTP uses the otpauth library (RFC 6238, compatible with any authenticator app).
 * All user/session data lives in IndexedDB (managed by storage.ts).
 */

import * as OTPAuth from 'otpauth';
import { getDB } from './storage';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface LocalUser {
  id: string;
  email: string;
  displayName?: string;
  passwordHash: string;   // base64 PBKDF2 digest
  passwordSalt: string;   // base64 random salt
  mfaEnabled: boolean;
  mfaSecret?: string;     // base32 TOTP secret (only present if mfaEnabled)
  createdAt: string;
  migratedFrom?: string;  // 'bitbucket' | 'github' — informational only
}

export interface LocalSession {
  token: string;
  userId: string;
  email: string;
  displayName?: string;
  createdAt: string;
  lastActive: string;
}

// ─── Auth-specific IndexedDB ──────────────────────────────────────────────────
// Uses the shared getDB() from storage.ts (same 'verigit' DB, single connection).
// The users/sessions stores are created in storage.ts's upgrade handler.

// ─── Session storage key ───────────────────────────────────────────────────────
const SESSION_KEY = 'verigit-session-token';

// ─── Password hashing (PBKDF2-SHA256) ─────────────────────────────────────────

function randomBytes(length: number): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(length));
}

function toBase64(buf: Uint8Array): string {
  return btoa(String.fromCharCode(...buf));
}

function fromBase64(s: string): Uint8Array {
  return Uint8Array.from(atob(s), c => c.charCodeAt(0));
}

async function pbkdf2Hash(password: string, salt: Uint8Array): Promise<string> {
  const enc = new TextEncoder();
  const passwordBuf = enc.encode(password);
  const keyMaterial = await crypto.subtle.importKey(
    'raw', passwordBuf.buffer.slice(passwordBuf.byteOffset, passwordBuf.byteOffset + passwordBuf.byteLength) as ArrayBuffer,
    'PBKDF2', false, ['deriveBits'],
  );
  const saltBuf = salt.buffer.slice(salt.byteOffset, salt.byteOffset + salt.byteLength) as ArrayBuffer;
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', salt: saltBuf, iterations: 600_000, hash: 'SHA-256' },
    keyMaterial,
    256,
  );
  return toBase64(new Uint8Array(bits));
}

export async function hashPassword(password: string): Promise<{ hash: string; salt: string }> {
  const salt = randomBytes(32);
  const hash = await pbkdf2Hash(password, salt);
  return { hash, salt: toBase64(salt) };
}

export async function verifyPassword(password: string, hash: string, salt: string): Promise<boolean> {
  const computed = await pbkdf2Hash(password, fromBase64(salt));
  return computed === hash;
}

// ─── TOTP ──────────────────────────────────────────────────────────────────────

export function generateTOTPSecret(): string {
  const totp = new OTPAuth.TOTP({ issuer: 'VeriGit', algorithm: 'SHA1', digits: 6, period: 30 });
  return totp.secret.base32;
}

export function getTOTPUri(secret: string, email: string): string {
  const totp = new OTPAuth.TOTP({
    issuer: 'VeriGit',
    label: email,
    algorithm: 'SHA1',
    digits: 6,
    period: 30,
    secret: OTPAuth.Secret.fromBase32(secret),
  });
  return totp.toString();
}

export function verifyTOTP(secret: string, code: string): boolean {
  const totp = new OTPAuth.TOTP({
    issuer: 'VeriGit',
    algorithm: 'SHA1',
    digits: 6,
    period: 30,
    secret: OTPAuth.Secret.fromBase32(secret),
  });
  const delta = totp.validate({ token: code.replace(/\s/g, ''), window: 1 });
  return delta !== null;
}

// ─── User CRUD ─────────────────────────────────────────────────────────────────

export async function getUserByEmail(email: string): Promise<LocalUser | null> {
  const db = await getDB();
  const user = await db.getFromIndex('users', 'by_email', email.toLowerCase().trim());
  return user ?? null;
}

export async function getUserById(id: string): Promise<LocalUser | null> {
  const db = await getDB();
  const user = await db.get('users', id);
  return user ?? null;
}

export async function getAllUsers(): Promise<LocalUser[]> {
  const db = await getDB();
  return db.getAll('users');
}

/** Restores exported user records directly (preserving original passwordHash/Salt).
 *  Skips any user whose email already exists. Returns the count of records written. */
export async function importUsersFromExport(users: LocalUser[]): Promise<number> {
  const db = await getDB();
  let count = 0;
  for (const user of users) {
    const existing = await db.getFromIndex('users', 'by_email', user.email.toLowerCase().trim());
    if (!existing) {
      await db.put('users', user);
      count++;
    }
  }
  return count;
}

export async function createUser(
  email: string,
  password: string,
  displayName?: string,
  migratedFrom?: string,
): Promise<LocalUser> {
  const existing = await getUserByEmail(email);
  if (existing) throw new Error('An account with that email already exists.');

  const { hash, salt } = await hashPassword(password);
  const user: LocalUser = {
    id: crypto.randomUUID(),
    email: email.toLowerCase().trim(),
    displayName: displayName || email.split('@')[0],
    passwordHash: hash,
    passwordSalt: salt,
    mfaEnabled: false,
    createdAt: new Date().toISOString(),
    migratedFrom,
  };
  const db = await getDB();
  await db.put('users', user);
  return user;
}

export async function updateUser(id: string, updates: Partial<Omit<LocalUser, 'id' | 'createdAt'>>): Promise<LocalUser> {
  const db = await getDB();
  const existing = await db.get('users', id) as LocalUser | undefined;
  if (!existing) throw new Error('User not found.');
  const updated = { ...existing, ...updates };
  await db.put('users', updated);
  return updated;
}

// ─── Authentication ────────────────────────────────────────────────────────────

export interface AuthResult {
  success: boolean;
  error?: string;
  requiresMFA?: boolean;
  session?: LocalSession;
}

export async function authenticate(
  email: string,
  password: string,
  totpCode?: string,
): Promise<AuthResult> {
  const user = await getUserByEmail(email);
  if (!user) return { success: false, error: 'No account found with that email address.' };

  const valid = await verifyPassword(password, user.passwordHash, user.passwordSalt);
  if (!valid) return { success: false, error: 'Incorrect password.' };

  if (user.mfaEnabled) {
    if (!totpCode) return { success: false, requiresMFA: true };
    if (!user.mfaSecret || !verifyTOTP(user.mfaSecret, totpCode)) {
      return { success: false, error: 'Invalid authenticator code. Please try again.' };
    }
  }

  const session = await createSession(user);
  return { success: true, session };
}

// ─── Sessions ──────────────────────────────────────────────────────────────────

export async function createSession(user: LocalUser): Promise<LocalSession> {
  const tokenBytes = randomBytes(32);
  const token = Array.from(tokenBytes).map(b => b.toString(16).padStart(2, '0')).join('');
  const session: LocalSession = {
    token,
    userId: user.id,
    email: user.email,
    displayName: user.displayName,
    createdAt: new Date().toISOString(),
    lastActive: new Date().toISOString(),
  };
  const db = await getDB();
  await db.put('sessions', session);
  localStorage.setItem(SESSION_KEY, token);
  return session;
}

export async function getActiveSession(): Promise<LocalSession | null> {
  const token = localStorage.getItem(SESSION_KEY);
  if (!token) return null;
  const db = await getDB();
  const session = await db.get('sessions', token) as LocalSession | undefined;
  if (!session) return null;
  // Touch lastActive
  await db.put('sessions', { ...session, lastActive: new Date().toISOString() });
  return session;
}

export async function endSession(): Promise<void> {
  const token = localStorage.getItem(SESSION_KEY);
  if (token) {
    const db = await getDB();
    await db.delete('sessions', token);
  }
  localStorage.removeItem(SESSION_KEY);
}

// ─── Remember-me device tokens ────────────────────────────────────────────────

const REMEMBER_ME_KEY = (userId: string) => `verigit-rm-${userId}`;
const REMEMBER_ME_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

export function saveRememberMeToken(userId: string): void {
  const token = Array.from(randomBytes(32)).map(b => b.toString(16).padStart(2, '0')).join('');
  localStorage.setItem(REMEMBER_ME_KEY(userId), JSON.stringify({ token, expires: Date.now() + REMEMBER_ME_TTL_MS }));
}

function getRememberMeEntry(userId: string): { token: string; expires: number } | null {
  try {
    const raw = localStorage.getItem(REMEMBER_ME_KEY(userId));
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (Date.now() > entry.expires) { localStorage.removeItem(REMEMBER_ME_KEY(userId)); return null; }
    return entry;
  } catch { return null; }
}

export function clearRememberMeToken(userId: string): void {
  localStorage.removeItem(REMEMBER_ME_KEY(userId));
}

/** Authenticate via saved device token (no password required).
 *  Returns requiresMFA=true if the account has TOTP enabled — caller must collect the code
 *  and then call authenticateWithRememberMeAndMFA(). */
export async function authenticateWithRememberMe(userId: string): Promise<AuthResult> {
  const entry = getRememberMeEntry(userId);
  if (!entry) return { success: false, error: 'No saved login for this account on this device.' };
  const user = await getUserById(userId);
  if (!user) return { success: false, error: 'Account not found.' };
  if (user.mfaEnabled) return { success: false, requiresMFA: true };
  const session = await createSession(user);
  return { success: true, session };
}

/** Second step for remember-me + MFA: validates TOTP code then creates the session. */
export async function authenticateWithRememberMeAndMFA(userId: string, totpCode: string): Promise<AuthResult> {
  const entry = getRememberMeEntry(userId);
  if (!entry) return { success: false, error: 'No saved login for this account on this device.' };
  const user = await getUserById(userId);
  if (!user) return { success: false, error: 'Account not found.' };
  if (!user.mfaEnabled || !user.mfaSecret) return { success: false, error: 'MFA not configured.' };
  if (!verifyTOTP(user.mfaSecret, totpCode)) return { success: false, error: 'Invalid authenticator code. Please try again.' };
  const session = await createSession(user);
  return { success: true, session };
}

// ─── Migration helpers ─────────────────────────────────────────────────────────

export const LEGACY_AUTH_KEY = 'verigit-auth';
export const LEGACY_PROFILES_KEY = 'verigit-saved-profiles';

export interface LegacyAuth {
  email: string;
  token?: string;
  bitbucketUsername?: string;
  githubUsername?: string;
  githubToken?: string;
}

export function getLegacyAuth(): LegacyAuth | null {
  try {
    const raw = localStorage.getItem(LEGACY_AUTH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if ((parsed.email && parsed.token) || parsed.githubToken) return parsed;
  } catch {}
  return null;
}

export function clearLegacyAuth(): void {
  localStorage.removeItem(LEGACY_AUTH_KEY);
  localStorage.removeItem(LEGACY_PROFILES_KEY);
  localStorage.removeItem('verigit-skip-login');
}

export async function isFirstRun(): Promise<boolean> {
  const users = await getAllUsers();
  return users.length === 0;
}
